import request from "project-config/services/request/request";
import { message } from "antd";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import useForm from "antd/es/form/hooks/useForm";
import { useDeveloperStore } from "../store/developer-store";
import { STORAGE } from "project-config/constants";
import { useState } from "react";
import { IAccessKeyInfo } from "../types";
import { useWatch } from "antd/es/form/Form";

interface IAccessForm {
  url: string;
}

interface IVerifyForm {
  uid: string;
  email?: string;
  phone?: string;
  verificationCode: string;
}

export function useDeveloper() {
  const [form] = useForm<IAccessForm>();
  const [verifyForm] = useForm<IVerifyForm>();
  const [openCloseServiceModal, setOpenCloseServiceModal] = useState(false);
  const [openVerifyModal, setOpenVerifyModal] = useState(false);
  const [accessKeyInfo, setAccessKeyInfo] = useState<IAccessKeyInfo>();
  const t = getTranslateNsT("center");
  const { access, setAccess, fetchAccessInfo } = useDeveloperStore();
  const url = useWatch("url", form);

  const changeAccessKeyState = async (allow: boolean) => {
    const res = await request.post(
      "/api/rendering/user/developer/toggleAccessKeyState",
      {
        allow: Number(allow),
      }
    );
    if (res && res.code === 200) {
      setAccess({
        ...access,
        allowAccess: Number(allow),
      });
      message.success(
        allow ? t("user_accessKey_abled") : t("user_accessKey_disabled")
      );
    }
    if (!allow) setOpenCloseServiceModal(false);
  };

  const handleSwitch = async () => {
    const allow = Number(!Boolean(access?.allowAccess));
    if (!allow) {
      setOpenCloseServiceModal(true);
      return;
    }
    changeAccessKeyState(true);
  };

  const sendUrl = async () => {
    const res = await request.post("/api/rendering/task/callback/addUrl", {
      url: form.getFieldValue("url"),
    });
    if (res && res.code === 200) {
      message.success(t("user_button_send_url_success"));
    } else {
      message.error(t("user_button_send_url_fail"));
    }
  };

  const createAccessKey = async () => {
    const res = await request.post(
      "/api/rendering/user/developer/createAccessKey"
    );
    if (res && res.code === 200) {
      message.success(t("user_button_create_key_success"));
      fetchAccessInfo();
    } else {
      message.error(t("user_button_create_key_error"));
    }
  };

  const storageAccessKeyInfo = (data: IAccessKeyInfo) => {
    setAccessKeyInfo(data);
    localStorage.setItem(STORAGE.UID, data.uid);
  };

  const deleteAccessKey = async () => {
    const res = await request.post(
      "/api/rendering/user/developer/deleteAccessKey",
      {
        uid: localStorage.getItem(STORAGE.UID),
      }
    );
    if (res && res.code === 200) {
      if (res.data.valid === "0") {
        storageAccessKeyInfo(res.data.info);
        setOpenVerifyModal(true);
      } else {
        message.success(t("user_deleted_success"));
        setAccess(null);
      }
    }
  };

  const handleShowAccessKey = async () => {
    if (access?.accessKey) {
      setAccess({
        ...access,
        accessKey: "",
      });
      return;
    }
    const res = await request.post(
      "/api/rendering/user/developer/queryAccessInfo",
      {
        uid: localStorage.getItem(STORAGE.UID),
      }
    );
    if (res && res.code === 200) {
      if (res.data.valid === "0") {
        storageAccessKeyInfo(res.data.info);
        setOpenVerifyModal(true);
      } else {
        setAccess({
          ...access,
          ...res.data.info,
        });
      }
    }
  };

  const handleSubmitVerify = (isDomestic: boolean) => {
    verifyForm.validateFields().then(async () => {
      const url = isDomestic
        ? "/api/rendering/user/validate/validateAccessKeyCode"
        : "/api/rendering/user/validate/emailValidateAccessKeyCode";
      const uid = localStorage.getItem(STORAGE.UID);
      const params = {
        uid,
        verificationCode: verifyForm.getFieldValue("verificationCode"),
        [isDomestic ? "phone" : "email"]: isDomestic
          ? accessKeyInfo?.phone
          : accessKeyInfo?.email,
      };
      const { code } = await request.post(url, params);
      if (code === 200) {
        setOpenVerifyModal(false);
        verifyForm.resetFields();
      } else if (code === 604 || code === 601) {
        verifyForm.setFieldValue("verificationCode", "");
        verifyForm.validateFields();
      }
    });
  };

  return {
    form,
    verifyForm,
    handleSwitch,
    sendUrl,
    createAccessKey,
    deleteAccessKey,
    handleShowAccessKey,
    openVerifyModal,
    setOpenVerifyModal,
    changeAccessKeyState,
    accessKeyInfo,
    openCloseServiceModal,
    setOpenCloseServiceModal,
    handleSubmitVerify,
    url,
  };
}
